import {
  ReportError,
  ReportLoading,
  ReportMetricCard,
  ReportPageHeader,
  formatMoneyTotals,
} from "@/components/reports/report-page";
import { UOMReviewCard } from "@/components/reports/uom-review-card";
import { buildMetadata } from "@/lib/metadata";
import { ReportService } from "@/services/report-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Box, DollarSign, MessageCircle } from "lucide-react";
import { normalizeReportSearch, reportSearchSchema } from "./-report-search";

export const Route = createFileRoute("/_main/reports/uom-mismatch")({
  validateSearch: reportSearchSchema,
  component: UOMMismatchRoute,
  head: () => ({
    meta: buildMetadata({
      title: "UOM Mismatch Report",
      description: "Review UOM distinctions and processing readiness",
      keywords: ["reports", "uom", "orders"],
    }),
  }),
});

function UOMMismatchRoute() {
  const range = normalizeReportSearch(Route.useSearch());
  const navigate = Route.useNavigate();
  const reportQuery = useQuery({
    queryKey: ["reports", "uom-mismatch", range.dateFrom, range.dateTo],
    queryFn: () => ReportService.getUOMMismatch(range),
  });
  const report = reportQuery.data;

  return (
    <main className="space-y-7 p-4 md:p-8">
      <ReportPageHeader
        title="UOM Mismatch Report"
        totalOrders={report?.mismatched_orders}
        range={range}
        onRangeChange={(nextRange) =>
          navigate({ search: nextRange, replace: true })
        }
      />

      {reportQuery.isLoading ? <ReportLoading /> : null}
      {reportQuery.isError ? <ReportError /> : null}

      {report ? (
        <>
          <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <ReportMetricCard
              label="UOM Review Orders"
              value={report.mismatched_orders}
              detail="Orders with UOM distinctions or missing configuration"
              icon={AlertTriangle}
              iconClassName="text-red-400"
            />
            <ReportMetricCard
              label="Affected SKUs"
              value={report.affected_skus}
              detail="Across matched products"
              icon={Box}
            />
            <ReportMetricCard
              label="Top Channel"
              value={report.top_channel ?? "—"}
              icon={MessageCircle}
            />
            <ReportMetricCard
              label="Value at Risk"
              value={formatMoneyTotals(report.value_at_risk)}
              detail="From source order amounts where available"
              icon={DollarSign}
            />
          </section>

          <UOMReviewCard rows={report.rows} range={range} />
        </>
      ) : null}
    </main>
  );
}
